import React from 'react';
import {
  INQUIRY_PHONE,
  INQUIRY_KAKAO_CHANNEL_URL,
  handleKakaoChatClick,
} from '../../domain/inquiry/PHONE';
import { DEFAULT_CONTACT_PHONE, type ContactPhone } from '../../domain/inquiry/resolveContactPhone';

/**
 * InquiryCTA — 부냥 통합 문의 CTA.
 *
 * 정책 (v2 개정 2026-09-21):
 *   - phone prop 미지정 시 대표번호 1533-8124 (기본값). 다른 번호는 오직 phone prop 으로만.
 *   - phone 은 반드시 resolveContactPhone(property) 결과를 넘긴다 (사이드문의=대표번호, 담당자=현장번호).
 *   - 카톡 링크 referrerPolicy="unsafe-url" 유지 (noreferrer 금지).
 *
 * 변형:
 *   - sticky (하단 고정 2버튼)
 *   - inline (섹션 내 단일 버튼)
 *   - compact (작은 텍스트 CTA)
 */

export type InquiryCTAVariant = 'sticky' | 'inline' | 'compact';

export interface InquiryCTAProps {
  variant?: InquiryCTAVariant;
  /** 전화 액션 라벨 (기본 "전화 문의") */
  callLabel?: string;
  /** 카톡 액션 라벨 (기본 "카톡 상담") */
  kakaoLabel?: string;
  /** 카톡 채널 URL — 미지정 시 기본 부냥 채널. 정책상 외부 도메인 허용 (kakao). */
  kakaoUrl?: string;
  /** 카톡 액션 숨김 (전화만) */
  callOnly?: boolean;
  /** 비활성 상태 (예: 자료 도착 시 알림으로 대체) */
  disabled?: boolean;
  /** 비활성 시 대체 라벨 */
  disabledLabel?: string;
  /** 전화번호 — resolveContactPhone 결과. 미지정 시 대표번호 1533-8124 */
  phone?: Pick<ContactPhone, 'display' | 'tel'> & { isDefault?: boolean };
  /** sticky: 관심(찜) 버튼 — 지정 시 목업 .ctabar 좌측 하트 버튼 노출 */
  onFavoriteToggle?: () => void;
  isFavorited?: boolean;
  /** 관심등록 수 (서버가 10 미만이면 null → 숫자 미표시) */
  favoriteCount?: number | null;
  /** 상세 화면 비교 등 보조 액션. 기존 관심 버튼 대신 표시. */
  secondaryAction?: React.ReactNode;
  className?: string;
}

const PhoneIcon: React.FC<{ size?: number }> = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const ChatIcon: React.FC<{ size?: number }> = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
  </svg>
);

const HeartIcon: React.FC<{ filled?: boolean }> = ({ filled }) => (
  <svg width={18} height={18} viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);

export const InquiryCTA: React.FC<InquiryCTAProps> = ({
  variant = 'sticky',
  phone = DEFAULT_CONTACT_PHONE,
  callLabel,
  kakaoLabel = '카톡 상담',
  kakaoUrl = INQUIRY_KAKAO_CHANNEL_URL,
  callOnly = false,
  disabled = false,
  disabledLabel = '자료 도착 시 알림',
  onFavoriteToggle,
  isFavorited = false,
  favoriteCount,
  secondaryAction,
  className = '',
}) => {
  const phoneDisplay = phone.display;
  const phoneTel = phone.tel;
  const isDefaultPhone = phone.isDefault ?? phoneDisplay === INQUIRY_PHONE;
  // sticky: 번호 그대로 / inline: "전화 문의" (호출부 override 가능)
  const numberLabel = callLabel ?? phoneDisplay;

  if (disabled) {
    return (
      <div
        className={
          variant === 'sticky'
            ? `sticky bottom-[64px] md:bottom-0 z-10 flex gap-2 border-t border-ca-line bg-frame/95 px-3.5 py-2.5 backdrop-blur ${className}`
            : `flex gap-2 ${className}`
        }
        style={{ paddingBottom: 'calc(11px + env(safe-area-inset-bottom, 0px))' }}
      >
        <button
          type="button"
          disabled
          className="flex-1 rounded-ca-xl border border-ca-line-strong bg-ca-surface px-4 py-3.5 text-[14px] font-extrabold text-muted"
        >
          {disabledLabel}
        </button>
      </div>
    );
  }

  // sticky variant — v2 목업 .ctabar: [관심] [카톡 상담(dark)] [현장 직통/전화 상담(오렌지, 번호)]
  // 목업 '방문예약'은 예약 API 가 없어 기존 전환 채널인 카톡 상담으로 유지 (계획서 3장 F).
  // lg+(1024px) 이상에서는 자동 숨김 — GNB 전화 CTA + 우측 ContactStickyCard 가 대체
  if (variant === 'sticky') {
    return (
      <div
        className={`sticky bottom-[64px] md:bottom-0 z-10 flex items-center gap-2 border-t border-ca-line-soft bg-frame px-4 py-2.5 lg:hidden ${className}`}
        style={{
          paddingBottom: 'calc(10px + env(safe-area-inset-bottom, 0px))',
          boxShadow: '0 -4px 20px rgba(15,23,42,.1)',
        }}
      >
        {secondaryAction ?? (onFavoriteToggle && (
          <button
            type="button"
            onClick={onFavoriteToggle}
            aria-label={isFavorited ? '관심 해제' : '관심 등록'}
            className={`flex h-11 w-[52px] shrink-0 flex-col items-center justify-center rounded-ca-xl bg-ca-surface ${
              isFavorited ? 'text-accent' : 'text-ink'
            }`}
          >
            <HeartIcon filled={isFavorited} />
            {typeof favoriteCount === 'number' && (
              <span className="num text-[10px] font-bold leading-none">{favoriteCount.toLocaleString()}</span>
            )}
          </button>
        ))}
        {!callOnly && (
          <a
            href={kakaoUrl}
            target="_blank"
            rel="noopener"
            referrerPolicy="unsafe-url"
            onClick={handleKakaoChatClick}
            className="flex h-11 flex-1 items-center justify-center gap-1.5 rounded-ca-xl border border-ca-line-strong bg-frame px-2 text-[13px] font-semibold text-ink active:translate-y-[1px]"
          >
            <ChatIcon />
            <span>{kakaoLabel}</span>
          </a>
        )}
        <a
          href={phoneTel}
          aria-label={`전화 문의 ${phoneDisplay}`}
          className="flex h-11 flex-[1.4] flex-col items-center justify-center rounded-ca-xl bg-accent px-3 text-white shadow-ca-cta active:translate-y-[1px]"
        >
          <span className="text-[10px] font-semibold leading-tight opacity-90">
            {isDefaultPhone ? '전화 상담' : '현장 직통 상담'}
          </span>
          <span className="num flex items-center gap-1 text-[15px] font-bold leading-tight">
            <PhoneIcon size={13} />
            <span>{numberLabel}</span>
          </span>
        </a>
      </div>
    );
  }

  // inline variant — 좌우 동일 너비 (작은 섹션 내부)
  const callBtn = (
    <a
      href={phoneTel}
      aria-label={`전화 문의 ${phoneDisplay}`}
      className="flex flex-1 items-center justify-center gap-2 rounded-ca-xl bg-accent px-4 py-3.5 text-[14px] font-extrabold text-white shadow-ca-cta active:translate-y-[1px]"
    >
      <PhoneIcon />
      <span>{callLabel ?? '전화 문의'}</span>
    </a>
  );

  const kakaoBtn = !callOnly && (
    <a
      href={kakaoUrl}
      target="_blank"
      rel="noopener"
      referrerPolicy="unsafe-url"
      onClick={handleKakaoChatClick}
      className="flex flex-1 items-center justify-center gap-2 rounded-ca-xl border border-ca-line-strong bg-frame px-4 py-3.5 text-[14px] font-extrabold text-ink active:translate-y-[1px]"
    >
      <ChatIcon />
      <span>{kakaoLabel}</span>
    </a>
  );

  if (variant === 'compact') {
    return (
      <a
        href={phoneTel}
        className={`num inline-flex items-center gap-1.5 rounded-ca-md bg-accent-50 px-2.5 py-1.5 text-[11.5px] font-bold text-accent-dark ${className}`}
        aria-label={`${callLabel ?? '전화 문의'} ${phoneDisplay}`}
      >
        <PhoneIcon />
        {phoneDisplay}
      </a>
    );
  }

  // inline
  return (
    <div className={`flex gap-2 ${className}`}>
      {callBtn}
      {kakaoBtn}
    </div>
  );
};

export default InquiryCTA;
